import { __decorate } from "tslib";
import { css, html, LitElement, nothing } from 'lit';
import { property, state, query } from 'lit/decorators.js';
import { map } from 'lit/directives/map.js';
import { emit } from '../../internal/event.js';
import { watch } from '../../internal/watch.js';
import { mobileBreakpoint } from '../../internal/preferences.js';
import componentStyles from '../../styles/component.styles.js';
import { ARC_EVENTS } from '../../internal/constants/eventConstants.js';
import { ICON_TYPES } from '../icon/constants/IconConstants.js';
import '../dropdown/arc-dropdown.js';
import '../menu/arc-menu.js';
import '../menu-item/arc-menu-item.js';
import '../icon-button/arc-icon-button.js';
import { arupLogo } from './arup-logo.js';
export default class ArcNavbar extends LitElement {
    constructor() {
        super(...arguments);
        this.showDropdown = false;
        this.navTabs = [];
        this.tabs = 5;
        this.arup = true;
    }
    handleTabCountChange() {
        this.updateTemplate();
    }
    handleTabChange(e) {
        const nodes = e.target.assignedElements({ flatten: true });
        this.navTabs = nodes.filter((el) => el.tagName === 'ARC-BUTTON' || el.tagName === 'ARC-ICON-BUTTON');
        this.updateTemplate();
    }
    updateTemplate() {
        this.showDropdown = this.navTabs.length > this.tabs;
        [...this.navTabs].forEach(tab => {
            tab.style.display = this.showDropdown ? 'none' : 'initial';
        });
    }
    emitAccessibility() {
        emit(this, ARC_EVENTS.showAccessibility);
    }
    render() {
        const menuInterior = html `
      ${map(this.navTabs, tab => html `
          <arc-menu-item ?disabled="${tab.disabled}" @click="${() => tab.click()}">
            ${tab.name
            ? html `<arc-icon name="${tab.name}" slot="prefix"></arc-icon>`
            : nothing}
            ${tab.textContent || tab.label || tab.name || 'Invalid label'}
          </arc-menu-item>
        `)}
    `;
        const logoInterior = html `
      ${this.logo ? html `<img id="tool-logo" src="${this.logo}" alt="tool-logo" />` : nothing}
      <slot id="tool-name" name="name"></slot>
    `;
        return html `
      <header id="main">
        <div id="left">
          ${this.home
            ? html `
                <a id="logoWrapper" href=${this.home} rel="noreferrer noopener" role="button" aria-label="tool logo">
                  ${logoInterior}
                </a>
              `
            : html `<div id="logoWrapper">${logoInterior}</div>`}
        </div>
        <div id="right">
          <nav id="tabs" aria-label="primary navigation">
            <slot id="tabSlot" @slotchange=${this.handleTabChange}></slot>
            ${this.showDropdown
            ? html `
                  <arc-dropdown hoist>
                    <arc-icon-button slot="trigger" name=${ICON_TYPES.menu}></arc-icon-button>
                    <arc-menu>${menuInterior}</arc-menu>
                  </arc-dropdown>
                `
            : nothing}
            <arc-icon-button
              id="accessibility"
              name=${ICON_TYPES.accessibility}
              label="Accessibility panel"
              @click=${this.emitAccessibility}
            ></arc-icon-button>
            <slot name="user"></slot>
          </nav>
          ${this.arup ? html `<span id="company-logo">${arupLogo}</span>` : nothing}
        </div>
      </header>
    `;
    }
}
ArcNavbar.tag = 'arc-navbar';
ArcNavbar.styles = [
    componentStyles,
    css `
      :host {
        height: 3.5rem;
        background: rgb(var(--arc-container-color));
        z-index: 1;
        --logo-height: var(--arc-brand-height);
      }

      /* Layout */

      #main,
      #left,
      #logoWrapper,
      #right,
      #tabs {
        display: grid;
        grid-auto-flow: column;
        overflow: hidden;
      }

      #main {
        height: inherit;
        width: 100%;
        padding: 0 var(--arc-spacing-medium) 0 var(--arc-spacing-medium);
        box-shadow: var(--arc-box-shadow);
        user-select: none;
      }

      /* Left side */

      #left {
        justify-content: flex-start;
      }

      #logoWrapper {
        align-items: center;
        gap: var(--arc-spacing-small);
        text-decoration: none;
        color: inherit;
      }

      #tool-logo {
        height: var(--logo-height);
        width: auto;
      }

      /* Show the tool-name when there is no tool-logo */

      #tool-name {
        display: flex;
        overflow: hidden;
      }

      #tool-name::slotted(*) {
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
      }

      /* Hide the tool-name when there is a tool-logo */

      #tool-logo + #tool-name {
        display: none;
      }

      /* Right side */

      #right {
        justify-content: flex-end;
        gap: var(--arc-spacing-small);
      }

      #tabSlot {
        display: none;
      }

      ::slotted(arc-button) {
        border-left: var(--arc-border-width) var(--arc-border-style) rgb(var(--arc-color-default));
        border-right: var(--arc-border-width) var(--arc-border-style) rgb(var(--arc-color-default));
      }

      ::slotted(arc-icon-button) {
        margin: 0 var(--arc-spacing-x-small) 0 var(--arc-spacing-x-small);
      }

      #accessibility {
        display: none;
      }

      #company-logo {
        color: rgb(var(--arc-color-primary));
        display: flex;
        align-items: center;
      }

      #company-logo > svg {
        height: var(--arc-brand-height);
        width: auto;
      }

      /* Medium devices and up */
      @media (min-width: ${mobileBreakpoint}rem) {
        #tabSlot,
        #tool-logo + #tool-name,
        #accessibility {
          display: flex;
        }
      }
    `,
];
__decorate([
    query('#tabSlot')
], ArcNavbar.prototype, "tabSlot", void 0);
__decorate([
    state()
], ArcNavbar.prototype, "showDropdown", void 0);
__decorate([
    state()
], ArcNavbar.prototype, "navTabs", void 0);
__decorate([
    property({ type: String })
], ArcNavbar.prototype, "home", void 0);
__decorate([
    property({ type: String })
], ArcNavbar.prototype, "logo", void 0);
__decorate([
    property({ type: Number, reflect: true })
], ArcNavbar.prototype, "tabs", void 0);
__decorate([
    property({
        type: Boolean,
        reflect: true,
        converter: (attrValue) => (attrValue ? attrValue !== 'false' : true),
    })
], ArcNavbar.prototype, "arup", void 0);
__decorate([
    watch('tabs', { waitUntilFirstUpdate: true })
], ArcNavbar.prototype, "handleTabCountChange", null);
